import axios, { AxiosRequestConfig } from 'axios';

import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------
// AUTHENTICATION STATE MANAGEMENT
// ----------------------------------------------------------------------
/**
 * Global authentication state management to prevent API calls before token is ready.
 *
 * This prevents the common issue of:
 * 1. Component mounts
 * 2. SWR hook tries to fetch data
 * 3. Request fails with 403 (no token yet)
 * 4. Auth completes and sets token
 * 5. SWR retries the request
 *
 * Instead, SWR hooks will wait until isAuthReady is true before making any requests.
 *
 * Usage in SWR hooks:
 * ```typescript
 * import { conditionalSWRKey } from 'src/utils/axios';
 *
 * // For authenticated endpoints (most APIs):
 * useSWR(conditionalSWRKey('/api/endpoint'), fetcher);
 *
 * // For public endpoints (no auth required):
 * useSWR(conditionalSWRKey('/api/public-endpoint', false), fetcher);
 * ```
 */

export const axiosInstance = axios.create({ baseURL: HOST_API });

// Global flag to track if authentication is ready
let isAuthReady = false;

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosInstance.get(url, { ...config });
  return res.data;
};

/**
 * Sets the Authentication Bearer {jwt} in all requests.
 * @param jwt
 */
export const setJwtToHeaders = (jwt: string) => {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
  setAuthReady(true);
};

export const clearJwtFromHeaders = () => {
  delete axiosInstance.defaults.headers.common.Authorization;
  setAuthReady(false);
};

/**
 * Sets the authentication ready state.
 * @param ready
 */
export const setAuthReady = (ready: boolean) => {
  isAuthReady = ready;
};

/**
 * Returns whether authentication is ready.
 */
export const getAuthReady = () => isAuthReady;

/**
 * Creates a conditional SWR key that only fetches when authenticated.
 * Returns null if auth is not ready, preventing the request.
 * @param key - The SWR key (URL or array)
 * @param requireAuth - Whether this endpoint requires authentication (default: true)
 */
export const conditionalSWRKey = <T extends string | [string, AxiosRequestConfig] | null>(
  key: T,
  requireAuth: boolean = true
): T | null => {
  if (!requireAuth) return key;
  return isAuthReady ? key : null;
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  dashboard: {
    root: '/api/dashboard',
    events: '/api/dashboard/events',
    analytics: '/api/dashboard/analytics',
    custom_fields: '/api/custom-fields',
    custom_field_values: '/api/custom-field-values',
    reports: '/api/custom/report-builder/query',
    reports_config: '/api/custom/report-builder/config',
    reports_field_values: '/api/reports/field-values',
    report_definitions: '/api/report-definitions',
    company_partners: '/api/company-partners',
    company_projects: '/api/company-projects',
    company_project_actions: '/api/company-project-actions',
    company_project_categories: '/api/company-project-categories',
    companies: '/api/companies',
    user_invitations: '/api/user-invitations',
    companyDashboard: {
      overview: '/api/company-dashboard/overview',
      partnersCount: '/api/company-dashboard/partners-count',
      projects: '/api/company-dashboard/projects',
      actions: '/api/company-dashboard/actions',
      categorySummary: '/api/company-dashboard/category-summary',
      monthlyAnalytics: '/api/company-dashboard/monthly-analytics',
    },
    dataTransfer: {
      exportCompanyPartners: '/api/data-transfer/export/company-partners',
      exportCompanyProjects: '/api/data-transfer/export/company-projects',
      exportCompanyProjectActions: '/api/data-transfer/export/company-project-actions',
      importCompanyPartners: '/api/data-transfer/import/company-partners',
      importCompanyProjects: '/api/data-transfer/import/company-projects',
      importCompanyProjectActions: '/api/data-transfer/import/company-project-actions',
    },
  },
  auth: {
    me: '/api/auth/me',
    login: '/api/auth/login',
    register: '/api/auth/register',
  },
  user_after_login: {
    root: '/api/user-after-logins',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  event: {
    root: '/api/events/',
  },
  user_account: {
    root: '/api/user-accounts',
  },
  user_profile: {
    root: '/api/user-profiles',
  },
  user_page: {
    root: '/api/user-pages',
  },
  permissions: {
    me: '/api/permissions/me',
  },
  tag: {
    root: '/api/tags',
  },
  support_ticket: {
    root: '/api/support-tickets',
  },
  help_video: {
    root: '/api/help-videos',
  },
  config: '/api/config',
  feature_flags: '/api/feature-flags',
  translations: '/api/translations',
  upload: '/api/upload',
};
